import React, { useEffect, useState } from "react";
import {
  Radio,
  Bot,
  Brain,
  FileSpreadsheet,
  Server,
  Cloud,
  Wifi,
  HardDrive,
  BarChart3
} from "lucide-react";

// Data source icons and labels
const dataSources = [
  { icon: Radio, label: "SCADA / PLC Tags" },
  { icon: Wifi, label: "Sensor Streams" },
  { icon: Server, label: "Transaction DBs" },
  { icon: Cloud, label: "Cloud APIs" },
  { icon: FileSpreadsheet, label: "Fleet Logs" },
];

// Processing stages
const processingStages = [
  { icon: Bot, label: "Ingest & Structure", sublabel: "Pipelines" },
  { icon: HardDrive, label: "Store", sublabel: "Tiered Data Lake" },
  { icon: Brain, label: "Detect", sublabel: "Anomaly Models" },
  { icon: BarChart3, label: "Act", sublabel: "Automated Workflows" },
];

// Data Source Node Component
const DataSourceNode = ({ icon: Icon, label, delay }) => {
  const [active, setActive] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setActive(true);
      setTimeout(() => setActive(false), 600);
    }, 3000);

    const initialTimer = setTimeout(() => {
      setActive(true);
      setTimeout(() => setActive(false), 600);
    }, delay);

    return () => {
      clearInterval(timer);
      clearTimeout(initialTimer);
    };
  }, [delay]);

  return (
    <div className="flex flex-col items-center">
      <div
        className={`
          relative w-12 h-12 lg:w-14 lg:h-14 rounded-xl flex items-center justify-center
          transition-all duration-300 border-2
          ${active
            ? "bg-primary border-transparent scale-110 shadow-lg shadow-primary/30"
            : "bg-card border-border hover:border-primary/40"
          }
        `}
      >
        <Icon className={`w-6 h-6 lg:w-7 lg:h-7 transition-colors ${active ? "text-primary-foreground" : "text-muted-foreground"}`} />
        {active && (
          <div className="absolute inset-0 rounded-xl bg-primary animate-ping opacity-30" />
        )}
      </div>
      <span className="mt-2 text-xs text-muted-foreground font-medium text-center max-w-[80px]">{label}</span>
    </div>
  );
};

// Processing Stage Node
const ProcessingNode = ({ icon: Icon, label, sublabel, delay, isLast }) => {
  const [active, setActive] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setActive(true);
      setTimeout(() => setActive(false), 500);
    }, 3000);

    const initialTimer = setTimeout(() => {
      setActive(true);
      setTimeout(() => setActive(false), 500);
    }, delay);

    return () => {
      clearInterval(timer);
      clearTimeout(initialTimer);
    };
  }, [delay]);

  return (
    <div className="flex items-center">
      <div className="flex flex-col items-center">
        <div
          className={`
            relative w-16 h-16 lg:w-20 lg:h-20 rounded-2xl flex items-center justify-center
            transition-all duration-300
            ${active
              ? "bg-primary scale-110 shadow-lg shadow-primary/30"
              : "bg-card border-2 border-border hover:border-primary/40"
            }
          `}
        >
          <Icon className={`w-8 h-8 lg:w-10 lg:h-10 transition-colors ${active ? "text-primary-foreground" : "text-primary"}`} />
          {active && (
            <div className="absolute inset-0 rounded-2xl bg-primary animate-ping opacity-30" />
          )}
        </div>
        <h4 className="mt-3 text-sm lg:text-base font-semibold text-foreground">{label}</h4>
        <p className="text-xs text-muted-foreground">{sublabel}</p>
      </div>

      {!isLast && (
        <div className="hidden lg:flex items-center mx-4">
          <div className="w-12 h-0.5 bg-border relative overflow-hidden">
            <div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-primary to-transparent"
              style={{ animation: "flowRight 1.5s ease-in-out infinite" }}
            />
          </div>
          <div className="w-0 h-0 border-t-4 border-b-4 border-l-[6px] border-transparent border-l-primary/60" />
        </div>
      )}
    </div>
  );
};

export const DataFlowAnimation = () => {
  return (
    <div className="relative py-8">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent rounded-3xl" />

      {/* Main content */}
      <div className="relative z-10">
        {/* Data Sources Section */}
        <div className="mb-8">
          <p className="text-center text-sm font-medium text-muted-foreground uppercase tracking-wider mb-6">
            Multiple Data Sources
          </p>
          <div className="flex flex-wrap justify-center gap-4 lg:gap-6">
            {dataSources.map((source, index) => (
              <DataSourceNode
                key={source.label}
                icon={source.icon}
                label={source.label}
                delay={index * 300}
              />
            ))}
          </div>
        </div>

        {/* Flow arrows from sources to processing */}
        <div className="flex justify-center my-6">
          <div className="relative">
            {/* Converging lines */}
            <svg width="300" height="60" className="hidden lg:block">
              <defs>
                <linearGradient id="flowGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="hsl(168 76% 42%)" stopOpacity="0.3" />
                  <stop offset="100%" stopColor="hsl(168 76% 42%)" stopOpacity="0.8" />
                </linearGradient>
              </defs>
              <path d="M30 0 L150 50" stroke="url(#flowGrad)" strokeWidth="2" fill="none" />
              <path d="M90 0 L150 50" stroke="url(#flowGrad)" strokeWidth="2" fill="none" />
              <path d="M150 0 L150 50" stroke="url(#flowGrad)" strokeWidth="2" fill="none" />
              <path d="M210 0 L150 50" stroke="url(#flowGrad)" strokeWidth="2" fill="none" />
              <path d="M270 0 L150 50" stroke="url(#flowGrad)" strokeWidth="2" fill="none" />
              {/* Arrow head */}
              <polygon points="150,60 145,50 155,50" fill="hsl(168 76% 42%)" />
            </svg>

            {/* Mobile: simple arrow */}
            <div className="lg:hidden flex flex-col items-center">
              <div className="w-0.5 h-8 bg-gradient-to-b from-primary/40 to-primary" />
              <div className="w-0 h-0 border-l-4 border-r-4 border-t-[6px] border-transparent border-t-primary" />
            </div>
          </div>
        </div>

        {/* Processing Pipeline */}
        <div className="mb-4">
          <p className="text-center text-sm font-medium text-muted-foreground uppercase tracking-wider mb-6">
            Processing Pipeline
          </p>

          {/* Desktop: Horizontal */}
          <div className="hidden lg:flex justify-center items-start">
            {processingStages.map((stage, index) => (
              <ProcessingNode
                key={stage.label}
                icon={stage.icon}
                label={stage.label}
                sublabel={stage.sublabel}
                delay={1500 + index * 500}
                isLast={index === processingStages.length - 1}
              />
            ))}
          </div>

          {/* Mobile: Vertical */}
          <div className="lg:hidden flex flex-col items-center gap-4">
            {processingStages.map((stage, index) => (
              <React.Fragment key={stage.label}>
                <ProcessingNode
                  icon={stage.icon}
                  label={stage.label}
                  sublabel={stage.sublabel}
                  delay={1500 + index * 500}
                  isLast={true}
                />
                {index < processingStages.length - 1 && (
                  <div className="w-0.5 h-6 bg-gradient-to-b from-primary/60 to-primary/30" />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Animation keyframes */}
      <style>{`
        @keyframes flowRight {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }
      `}</style>
    </div>
  );
};

// Simplified version for smaller spaces
export const DataFlowMini = () => {
  return (
    <div className="py-4">
      {/* Data Sources */}
      <div className="flex justify-center gap-2 mb-3">
        {[Radio, FileSpreadsheet, Server, Cloud, Wifi].map((Icon, index) => (
          <div
            key={index}
            className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center"
            style={{ animation: `pulse 2s ease-in-out ${index * 0.2}s infinite` }}
          >
            <Icon className="w-4 h-4 text-primary" />
          </div>
        ))}
      </div>

      {/* Arrow */}
      <div className="flex justify-center mb-3">
        <div className="w-0.5 h-4 bg-gradient-to-b from-primary/40 to-primary" />
      </div>

      {/* Processing Pipeline */}
      <div className="flex items-center justify-center gap-2">
        {[Bot, HardDrive, Brain, BarChart3].map((Icon, index) => (
          <React.Fragment key={index}>
            <div
              className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center"
              style={{ animation: `pulse 2s ease-in-out ${index * 0.3 + 0.5}s infinite` }}
            >
              <Icon className="w-5 h-5 text-primary" />
            </div>
            {index < 3 && (
              <div className="w-4 h-0.5 bg-gradient-to-r from-primary/40 to-primary" />
            )}
          </React.Fragment>
        ))}
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }
      `}</style>
    </div>
  );
};
